/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { service } from "@ember/service";
import { Changeset, EmberChangeset } from "ember-changeset";
import lookupValidator from "ember-changeset-validations";
import { validatePresence } from "ember-changeset-validations/validators";
import type { ForcBaseArgs } from "parog-web/components/forc/abstract/base-component";
import ForcBaseComponent from "parog-web/components/forc/abstract/base-component";
import ConsultationFrequency from "parog-web/models/consultation/consultation-frequency";
import type MedicalActFrequencyChangedJournalEvent from "parog-web/models/medical-act-frequency-changed-journal-event";
import type ConfigurationService from "parog-web/services/configuration";
import { Configuration } from "parog-web/services/configuration";
import type SemeiaSessionService from "parog-web/services/session";
import validateSometimes from "parog-web/validators/sometimes";
import type { BufferedChangeset } from "validated-changeset";

// Follow the pattern described [here](https://github.com/poteto/ember-changeset/issues/551#issuecomment-728115650).
class ConsultationFrequenciesChangeset extends EmberChangeset {
  /**
   * Optional argument that can be given to filter which consultation-frequencies to show.
   */
  displayedConsultationFrequencies?: Array<ConsultationFrequency>;

  validations = {
    frequency: validateSometimes(
      [
        validatePresence({
          presence: true,
          description: "La fréquence"
        })
      ],
      function (this: BufferedChangeset) {
        /* eslint-disable-next-line */
        return this.get("enabled");
      }
    )
  };

  private declare _consultationFrequencies: Array<BufferedChangeset>;
  get consultationFrequencies(): Array<BufferedChangeset> {
    if (this._consultationFrequencies) return this._consultationFrequencies;

    // TODO: how can we redifine the type of getter `data` in the class?
    let consultationFrequenciesToDisplay = (
      this.data as { consultationFrequencies: ConsultationFrequency[] }
    ).consultationFrequencies;

    // Filters the consultation-frequencies to display.
    // This is used on MentalWise to display either "physical" consultation or "psychiatrist" consultation.
    const frequencyToDisplay = this.displayedConsultationFrequencies;
    if (frequencyToDisplay) {
      consultationFrequenciesToDisplay =
        consultationFrequenciesToDisplay.filter(consultationFrequency => {
          for (const displayedConsultationFrequency of frequencyToDisplay) {
            if (displayedConsultationFrequency.id == consultationFrequency.id) {
              return true;
            }
          }
          return false;
        });
    }

    this._consultationFrequencies = consultationFrequenciesToDisplay.map(
      consultationFrequency =>
        Changeset(
          consultationFrequency,
          lookupValidator(this.validations),
          this.validations
        )
    );

    return this._consultationFrequencies;
  }

  get isDirty(): boolean {
    return this.consultationFrequencies.some(changeset => changeset.isDirty);
  }

  get isPristine(): boolean {
    return this.consultationFrequencies.some(changeset => changeset.isPristine);
  }

  get isValid(): boolean {
    return this.consultationFrequencies.some(changeset => changeset.isValid);
  }

  get isInvalid(): boolean {
    return this.consultationFrequencies.some(changeset => changeset.isInvalid);
  }

  async save() {
    if (!this.isDirty) return;

    this._consultationFrequencies.forEach(changeset => changeset.save());
    return super.save();
  }
}

class MedicalActsChangeset extends EmberChangeset {
  validations = {
    frequency: validateSometimes(
      [
        validatePresence({
          presence: true,
          description: "La fréquence"
        })
      ],
      function (this: BufferedChangeset) {
        /* eslint-disable-next-line */
        return this.get("enabled");
      }
    )
  };

  private declare _medicalActFrequencies: Array<BufferedChangeset>;
  get medicalActFrequencies(): Array<BufferedChangeset> {
    if (this._medicalActFrequencies) return this._medicalActFrequencies;

    this._medicalActFrequencies = (
      this.data as MedicalActFrequencyChangedJournalEvent
    ).medicalActFrequencies.map(medicalActFrequency => {
      return Changeset(
        medicalActFrequency,
        lookupValidator(this.validations),
        this.validations
      );
    });

    return this._medicalActFrequencies;
  }

  get isDirty(): boolean {
    return this.medicalActFrequencies.some(changeset => changeset.isDirty);
  }

  async save() {
    if (!this.isDirty) return;

    this.source = "wise"; //update the source if the record is changed from Wise
    this._medicalActFrequencies.forEach(changeset => changeset.save());
    return super.save();
  }
}

interface Args extends ForcBaseArgs {
  consultationFrequencies: ConsultationFrequency[];
  title: string;
}

export default class ConsultationFrequencyEditionForm extends ForcBaseComponent<Args> {
  static fullComponentId = "consultation-frequency/edition-form";

  @service declare configuration: ConfigurationService;
  @service declare session: SemeiaSessionService;

  selectableFrequencies = ConsultationFrequency.validFrequencies;

  get title(): string {
    return this.args.title || "Plan de suivi des consultations & examens";
  }

  get lastMedicalActFrequencyJe() {
    return this.patient.medicalActFrequencyChangedJournalEvent;
  }

  private declare _consultationsChangeset: BufferedChangeset;
  get consultationsChangeset(): BufferedChangeset {
    if (this._consultationsChangeset) return this._consultationsChangeset;

    // Extend Changeset class: https://github.com/poteto/ember-changeset#extending-the-base-ember-changeset-class
    this._consultationsChangeset = Changeset(
      this.configuration.configurationForPatient(
        Configuration.Consultation,
        this.patient
      ),
      () => true,
      {},
      { changeset: ConsultationFrequenciesChangeset }
    );

    this._consultationsChangeset.displayedConsultationFrequencies =
      this.args.consultationFrequencies;

    return this._consultationsChangeset;
  }

  private declare _medicalActsChangeset: BufferedChangeset;
  get medicalActsChangeset(): BufferedChangeset | undefined {
    if (!this.lastMedicalActFrequencyJe) return;

    if (this._medicalActsChangeset) return this._medicalActsChangeset;

    // Extend Changeset class: https://github.com/poteto/ember-changeset#extending-the-base-ember-changeset-class
    this._medicalActsChangeset = Changeset(
      this.lastMedicalActFrequencyJe as MedicalActFrequencyChangedJournalEvent,
      () => true,
      {},
      { changeset: MedicalActsChangeset }
    );

    return this._medicalActsChangeset;
  }

  get isDirty(): boolean {
    return !this.medicalActsChangeset
      ? this.consultationsChangeset.isDirty
      : this.consultationsChangeset.isDirty ||
          this.medicalActsChangeset.isDirty;
  }

  async save(): Promise<void> {
    if (!this.medicalActsChangeset) {
      return await this.consultationsChangeset.save();
    }

    await Promise.all([
      this.consultationsChangeset.save(),
      this.medicalActsChangeset.save()
    ]);
  }
}
